
import {
  defineComponent,
  defineAsyncComponent,
  ref,
  onMounted,
  computed,
} from 'vue';

import numeral from 'numeral';

import { TRAFFIC_TYPES } from '@/libs/consts';

import type {
  GenericObject,
  Option,
  Projects,
} from '@/types';

import useUser from '@/composable/useUser';

import useEntriesNext from '@/composable/useEntries@next';

import useUrlState from '@/composable/useUrlState';

export default defineComponent({
  components: {
    User: defineAsyncComponent(() => import('@/components/User.vue')),
    Chartist: defineAsyncComponent(() => import('@/components/Chartist.vue')),
  },
  setup() {
    const { activeUser: user } = useUser();

    const {
      entriesLoading: projectsLoading,
      entries: projects,
      fetchEntries: fetchProjects,
    } = useEntriesNext<Projects>('/api/project/getProjects');

    const {
      entriesLoading: chartDataLoading,
      entries: chartData,
      fetchEntries: fetchChartData,
    } = useEntriesNext<GenericObject[]>('/api/statistics/staff/getStatisticsByAdvertisersActivity', []);

    const chartDataFilters = useUrlState('filters', {
      hour_offset: 24,
      traffic_type: [],
      project_id: [],
      advertiser_id: [],
    });

    const usersParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? chartDataFilters.project_id.join(',') : undefined,
    }));

    const showChartDataFilters = ref<boolean>(false);

    const fetchChartDataWrapper = async (): Promise<void> => fetchChartData(chartDataFilters);

    const onProjectChange = (): void => {
      chartDataFilters.advertiser_id = [];
    };

    const hourOffsetOptions: Option[] = [3, 6, 12, 24, 48, 72, 168, 336].map((value: number) => {
      const labelMap: { [key: number]: string; } = {
        48: '2 days',
        72: '3 days',
        168: '1 week',
        336: '2 weeks',
      };

      return {
        value,
        label: `${labelMap[value] || value}`,
      };
    });

    const axisYLabelInterpolationFunction = (value: number): string => numeral(value).format('$0.0[0000]');

    onMounted((): void => {
      Promise.all([
        user.isSuperAdmin() ? fetchProjects() : Promise.resolve(),
        fetchChartDataWrapper(),
      ]);
    });

    return {
      user,
      projectsLoading,
      projects,
      chartDataLoading,
      chartData,
      chartDataFilters,
      showChartDataFilters,
      usersParams,
      fetchChartDataWrapper,
      onProjectChange,
      hourOffsetOptions,
      axisYLabelInterpolationFunction,
      TRAFFIC_TYPES,
    };
  },
});
